import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useRef, useState } from 'react'

export type Queue = {
    get<DataT>(id: string): DataT | undefined
    has(id: string): boolean
    push<DataT>(id: string, data?: DataT): void
    update<DataT>(id: string, data?: DataT): void
    remove: (id: string) => void
    updated: Date // Can be used for state updates.
}

type Item<DataT = unknown> = {
    id: string
    data?: DataT
}

const QueueContext = createContext<Queue>(null)

export function GenerationQueueProvider(props: PropsWithChildren): ReactElement {
    // We can't use useState(), because we want to avoid frequent re-renders to propagate through the app.
    const queue = useRef<Item[]>([])
    const [updated, setUpdated] = useState<Date>(new Date())
    const setQueue = (items: Item[]) => {
        queue.current = items
        setUpdated(new Date())
    }

    return (
        <QueueContext.Provider
            value={{
                get<DataT>(id: string): DataT | undefined {
                    return queue.current.find(item => item.id === id)?.data as DataT | undefined
                },
                has(id: string): boolean {
                    return !!queue.current.find(item => item.id === id)
                },
                push<DataT>(id: string, data?: DataT) {
                    setQueue([...queue.current.filter(item => item.id !== id), { id, data }])
                },
                update<DataT>(id: string, data?: DataT) {
                    setQueue(queue.current.map(item => (item.id === id ? { id, data } : item)))
                },
                remove(id: string) {
                    setQueue([...queue.current.filter(item => item.id !== id)])
                },
                updated,
            }}
        >
            {props.children}
        </QueueContext.Provider>
    )
}

export default QueueContext
